import React, { useState, useMemo } from 'react';
import { Download, FileSpreadsheet, Eye, EyeOff, Search, X, Users, GraduationCap, Phone, Mail } from 'lucide-react';
import * as XLSX from 'xlsx';
import API_URL from '../config';

// Logique de classification conforme à generateTdbBuffer.js
function isMASI(e) {
  const f = e.filiere || '';
  if (f === 'MASI' || f === 'QHSE' || f === 'QHSSE') return true;
  if (f === 'LICENCE_PRO' || f === 'MASTER_PRO' || f === 'MASTER') {
    const spec = `${e.specialite || ''} ${e.specialiteLicencePro || ''} ${e.specialiteMasterPro || ''} ${e.option || ''}`;
    if (spec.includes('Logistique') || spec.includes('Logistics') || spec.includes('Achat') || spec.includes('Sourcing') || spec.includes('Approvisionnement') || spec.includes('Commercial') || spec.includes('Finance') || spec.includes('Business') || spec.includes('Management') || spec.includes('Industrielle') || spec.includes('industriel') || spec.includes('mécanique') || spec.includes('QHSE') || spec.includes('QHSSE') || spec.includes('Qualité') || spec.includes('Performance')) {
      return true;
    }
  }
  return false;
}

function isIRM(e) {
  const f = e.filiere || '';
  if (f === 'IRM' || f === 'GENIE_CIVIL' || f === 'MECATRONIQUE') return true;
  if (f === 'LICENCE_PRO' || f === 'MASTER_PRO' || f === 'MASTER') {
    return true;
  }
  return false;
}

function isIng(e) {
  return e.filiere === 'CYCLE_INGENIEUR' || e.typeFormation === 'CYCLE_INGENIEUR';
}

function matchKeywords(e, keywords) {
  const text = `${e.filiere || ''} ${e.option || ''} ${e.specialite || ''} ${e.specialiteIngenieur || ''} ${e.optionIngenieur || ''} ${e.specialiteLicencePro || ''} ${e.optionLicencePro || ''} ${e.specialiteMasterPro || ''} ${e.optionMasterPro || ''}`;
  return keywords.some(k => text.toLowerCase().includes(k.toLowerCase()));
}

const TDB_TEMPLATE = [
  // LICENCE
  { label: 'LICENCE', isSection: true },
  {
    label: 'GC Licence 3',
    section: 'LICENCE',
    level: 3,
    match: (e) => !isIng(e) && e.filiere !== 'IRM' && e.filiere !== 'MASI' && e.niveau == 3 && (e.filiere === 'GENIE_CIVIL' || matchKeywords(e, ['GC', 'Civil', 'BIM', 'Travaux', 'Bâtiment']))
  },
  {
    label: 'Mecatronique Licence 3',
    section: 'LICENCE',
    level: 3,
    match: (e) => !isIng(e) && e.filiere !== 'IRM' && e.filiere !== 'MASI' && e.niveau == 3 && (e.filiere === 'MECATRONIQUE' || matchKeywords(e, ['Mécatronique', 'MECATRONIQUE', 'Automatisme', 'Industriel', 'Mécanique', 'Mecanique']))
  },
  {
    label: 'QHSE 3',
    section: 'LICENCE',
    level: 3,
    match: (e) => !isIng(e) && e.filiere !== 'IRM' && e.filiere !== 'GENIE_CIVIL' && e.filiere !== 'MECATRONIQUE' && e.niveau == 3 && (
      e.filiere === 'QHSE' || e.filiere === 'QHSSE' ||
      matchKeywords(e, ['QHSE', 'QHSSE', 'Qualité', 'Hygiène', 'Environnement']) ||
      (matchKeywords(e, ['Sécurité']) && !matchKeywords(e, ['Cyber', 'Cybersécurité', 'Informatique', 'Réseaux', 'IT']))
    )
  },

  // MASTER
  { label: 'MASTER', isSection: true },
  {
    label: 'QHSE 4',
    section: 'MASTER',
    level: 4,
    match: (e) => !isIng(e) && e.filiere !== 'IRM' && e.filiere !== 'MASI' && e.filiere !== 'GENIE_CIVIL' && e.filiere !== 'MECATRONIQUE' && e.niveau == 4 && (
      matchKeywords(e, ['QHSE', 'QHSSE', 'Qualité', 'Hygiène', 'Environnement']) ||
      (matchKeywords(e, ['Sécurité']) && !matchKeywords(e, ['Cyber', 'Cybersécurité', 'Informatique', 'Réseaux', 'IT']))
    )
  },
  {
    label: 'QHSE 5',
    section: 'MASTER',
    level: 5,
    match: (e) => !isIng(e) && e.filiere !== 'IRM' && e.filiere !== 'MASI' && e.filiere !== 'GENIE_CIVIL' && e.filiere !== 'MECATRONIQUE' && e.niveau == 5 && (
      matchKeywords(e, ['QHSE', 'QHSSE', 'Qualité', 'Hygiène', 'Environnement']) ||
      (matchKeywords(e, ['Sécurité']) && !matchKeywords(e, ['Cyber', 'Cybersécurité', 'Informatique', 'Réseaux', 'IT']))
    )
  },
  {
    label: 'IEMS 4',
    section: 'MASTER',
    level: 4,
    match: (e) => !isIng(e) && e.filiere !== 'IRM' && e.filiere !== 'MASI' && e.filiere !== 'GENIE_CIVIL' && e.filiere !== 'MECATRONIQUE' && e.niveau == 4 && matchKeywords(e, ['IEMS', 'Intelligence Économique', 'Intelligence Economique', 'Management Stratégique', 'Management Strategique'])
  },
  {
    label: 'IEMS 5',
    section: 'MASTER',
    level: 5,
    match: (e) => !isIng(e) && e.filiere !== 'IRM' && e.filiere !== 'MASI' && e.filiere !== 'GENIE_CIVIL' && e.filiere !== 'MECATRONIQUE' && e.niveau == 5 && matchKeywords(e, ['IEMS', 'Intelligence Économique', 'Intelligence Economique', 'Management Stratégique', 'Management Strategique'])
  },

  // MASI
  { label: 'MASI', isSection: true },
  { label: 'TC 1', section: 'MASI', level: 1, match: (e) => isMASI(e) && e.niveau == 1 },
  { label: 'TC2', section: 'MASI', level: 2, match: (e) => isMASI(e) && e.niveau == 2 },
  { label: 'Finance 3', section: 'MASI', level: 3, match: (e) => isMASI(e) && e.niveau == 3 && matchKeywords(e, ['Finance', 'Audit', 'Gestion']) },
  { label: 'Marketing 3', section: 'MASI', level: 3, match: (e) => isMASI(e) && e.niveau == 3 && matchKeywords(e, ['Marketing', 'Commercial', 'Digital']) },
  { label: 'Logistique 3', section: 'MASI', level: 3, match: (e) => isMASI(e) && e.niveau == 3 && matchKeywords(e, ['Logistique', 'Logistics', 'Transport', 'Supply', 'Achat', 'Achats', 'Sourcing', 'Approvisionnement']) },
  { label: 'Sans Spécialité 3', section: 'MASI', level: 3, match: () => false },
  { label: 'TC 4', section: 'MASI', level: 4, match: (e) => isMASI(e) && e.niveau == 4 && matchKeywords(e, ['TC', 'Tronc', 'Commun', 'Management des Affaires', 'Master']) && !matchKeywords(e, ['Finance', 'Audit', 'Gestion', 'Marketing', 'Commercial', 'Digital', 'Logistique', 'Logistics', 'Transport', 'Supply', 'Achat', 'Achats', 'Sourcing', 'Approvisionnement', 'QHSE', 'QHSSE', 'Qualité', 'Hygiène', 'Sécurité', 'Environnement', 'Performance']) },
  { label: 'Finance 4', section: 'MASI', level: 4, match: (e) => isMASI(e) && e.niveau == 4 && matchKeywords(e, ['Finance', 'Audit', 'Gestion']) },
  { label: 'Marketing 4', section: 'MASI', level: 4, match: (e) => isMASI(e) && e.niveau == 4 && matchKeywords(e, ['Marketing', 'Commercial', 'Digital']) },
  { label: 'Logistique 4', section: 'MASI', level: 4, match: (e) => isMASI(e) && e.niveau == 4 && matchKeywords(e, ['Logistique', 'Logistics', 'Transport', 'Supply', 'Achat', 'Achats', 'Sourcing', 'Approvisionnement']) },
  { label: 'Sans Spécialité 4', section: 'MASI', level: 4, match: () => false },
  { label: 'Finance 5', section: 'MASI', level: 5, match: (e) => isMASI(e) && e.niveau == 5 && matchKeywords(e, ['Finance', 'Audit', 'Gestion']) },
  { label: 'Marketing 5', section: 'MASI', level: 5, match: (e) => isMASI(e) && e.niveau == 5 && matchKeywords(e, ['Marketing', 'Commercial', 'Digital']) },
  { label: 'Logistique 5', section: 'MASI', level: 5, match: (e) => isMASI(e) && e.niveau == 5 && matchKeywords(e, ['Logistique', 'Logistics', 'Transport', 'Supply', 'Achat', 'Achats', 'Sourcing', 'Approvisionnement']) },
  { label: 'Sans Spécialité 5', section: 'MASI', level: 5, match: () => false },
  { label: 'MASI Fallback 3', section: 'MASI', level: 3, isFallback: true, match: (e) => isMASI(e) && e.niveau == 3, fallbackTo: 'Sans Spécialité 3' },
  { label: 'MASI Fallback 4', section: 'MASI', level: 4, isFallback: true, match: (e) => isMASI(e) && e.niveau == 4, fallbackTo: 'TC 4' },
  { label: 'MASI Fallback 5', section: 'MASI', level: 5, isFallback: true, match: (e) => isMASI(e) && e.niveau == 5, fallbackTo: 'Sans Spécialité 5' },

  // IRM
  { label: 'IRM', isSection: true },
  { label: 'TC 1', section: 'IRM', level: 1, match: (e) => (isIRM(e) || e.filiere === 'GENIE_CIVIL' || e.filiere === 'MECATRONIQUE') && e.niveau == 1 },
  { label: 'TC2', section: 'IRM', level: 2, match: (e) => (isIRM(e) || e.filiere === 'GENIE_CIVIL' || e.filiere === 'MECATRONIQUE') && e.niveau == 2 },
  { label: 'DEV 3', section: 'IRM', level: 3, match: (e) => isIRM(e) && e.filiere !== 'MECATRONIQUE' && e.filiere !== 'GENIE_CIVIL' && e.niveau == 3 && matchKeywords(e, ['DEV', 'Développement', 'Gaming', 'JAVA', 'Web', 'Mobile', 'Full', 'Génie informatique', 'Innovation technologique', 'GIIT']) },
  { label: 'RCY 3', section: 'IRM', level: 3, match: (e) => isIRM(e) && e.filiere !== 'MECATRONIQUE' && e.filiere !== 'GENIE_CIVIL' && e.niveau == 3 && matchKeywords(e, ['RCY', 'Réseaux', 'Cybersécurité', 'Cloud', 'Systèmes et Réseaux', 'Systèmes & Réseaux', 'Administration des Systèmes']) },
  { label: 'IA3', section: 'IRM', level: 3, match: (e) => isIRM(e) && e.filiere !== 'MECATRONIQUE' && e.filiere !== 'GENIE_CIVIL' && e.niveau == 3 && matchKeywords(e, ['IA', 'AI', 'Intelligence', 'Data', 'Analytics']) },
  { label: 'Sans Spécialité 3', section: 'IRM', level: 3, match: () => false },
  { label: 'DEV 4', section: 'IRM', level: 4, match: (e) => isIRM(e) && e.niveau == 4 && matchKeywords(e, ['DEV', 'Développement', 'Logiciel', 'Big Data', 'Génie informatique', 'Innovation technologique', 'GIIT']) },
  { label: 'RCY 4', section: 'IRM', level: 4, match: (e) => isIRM(e) && e.niveau == 4 && matchKeywords(e, ['RCY', 'Réseaux', 'Cybersécurité', 'Audit']) },
  { label: 'IA 4', section: 'IRM', level: 4, match: (e) => isIRM(e) && e.niveau == 4 && matchKeywords(e, ['IA', 'AI', 'Intelligence', 'Data']) },
  { label: 'Sans Spécialité 4', section: 'IRM', level: 4, match: () => false },
  { label: 'DEV 5', section: 'IRM', level: 5, match: (e) => isIRM(e) && e.niveau == 5 && matchKeywords(e, ['DEV', 'Développement', 'Logiciel', 'Big Data', 'Génie informatique', 'Innovation technologique', 'GIIT']) },
  { label: 'RCY 5', section: 'IRM', level: 5, match: (e) => isIRM(e) && e.niveau == 5 && matchKeywords(e, ['RCY', 'Réseaux', 'Cybersécurité']) },
  { label: 'IA 5', section: 'IRM', level: 5, match: (e) => isIRM(e) && e.niveau == 5 && matchKeywords(e, ['IA', 'AI', 'Intelligence', 'Data']) },
  { label: 'Sans Spécialité 5', section: 'IRM', level: 5, match: () => false },
  { label: 'IRM Fallback 3', section: 'IRM', level: 3, isFallback: true, match: (e) => isIRM(e) && e.niveau == 3, fallbackTo: 'Sans Spécialité 3' },
  { label: 'IRM Fallback 4', section: 'IRM', level: 4, isFallback: true, match: (e) => isIRM(e) && e.niveau == 4, fallbackTo: 'Sans Spécialité 4' },
  { label: 'IRM Fallback 5', section: 'IRM', level: 5, isFallback: true, match: (e) => isIRM(e) && e.niveau == 5, fallbackTo: 'Sans Spécialité 5' },

  // Ingenierie
  { label: 'Ingenierie', isSection: true },
  { label: 'CP 1', section: 'Ingenierie', level: 1, match: (e) => isIng(e) && e.niveau == 1 },
  { label: 'CP 2', section: 'Ingenierie', level: 2, match: (e) => isIng(e) && e.niveau == 2 },
  { label: 'Mecatronique 1', section: 'Ingenierie', level: 3, match: (e) => isIng(e) && e.niveau == 3 && matchKeywords(e, ['Mécatronique', 'MECATRONIQUE', 'Automatique']) },
  { label: 'Mecatronique 2', section: 'Ingenierie', level: 4, match: (e) => isIng(e) && e.niveau == 4 && matchKeywords(e, ['Mécatronique', 'MECATRONIQUE', 'Automatique']) },
  { label: 'Mecatronique 3', section: 'Ingenierie', level: 5, match: (e) => isIng(e) && e.niveau == 5 && matchKeywords(e, ['Mécatronique', 'MECATRONIQUE', 'Automatique']) },
  { label: 'IT 1', section: 'Ingenierie', level: 3, match: (e) => isIng(e) && e.niveau == 3 && matchKeywords(e, ['Informatique', 'GI', 'GENIE_INFORMATIQUE', 'IA', 'Réseaux', 'Dev', 'Logiciel']) },
  { label: 'IT 2', section: 'Ingenierie', level: 4, match: (e) => isIng(e) && e.niveau == 4 && matchKeywords(e, ['Informatique', 'GI', 'GENIE_INFORMATIQUE', 'IA', 'Réseaux', 'Dev', 'Logiciel']) },
  { label: 'IT 3', section: 'Ingenierie', level: 5, match: (e) => isIng(e) && e.niveau == 5 && matchKeywords(e, ['Informatique', 'GI', 'GENIE_INFORMATIQUE', 'IA', 'Réseaux', 'Dev', 'Logiciel']) },
  { label: 'GC1', section: 'Ingenierie', level: 3, match: (e) => isIng(e) && e.niveau == 3 && matchKeywords(e, ['Civil', 'GC', 'GENIE_CIVIL', 'Bâtiment', 'Ouvrages']) },
  { label: 'GC2', section: 'Ingenierie', level: 4, match: (e) => isIng(e) && e.niveau == 4 && matchKeywords(e, ['Civil', 'GC', 'GENIE_CIVIL', 'Bâtiment', 'Ouvrages']) },
  { label: 'GC3', section: 'Ingenierie', level: 5, match: (e) => isIng(e) && e.niveau == 5 && matchKeywords(e, ['Civil', 'GC', 'GENIE_CIVIL', 'Bâtiment', 'Ouvrages']) },
  { label: 'ING Fallback 3', section: 'Ingenierie', level: 3, isFallback: true, match: (e) => isIng(e) && e.niveau == 3, fallbackTo: 'IT 1' },
  { label: 'ING Fallback 4', section: 'Ingenierie', level: 4, isFallback: true, match: (e) => isIng(e) && e.niveau == 4, fallbackTo: 'IT 2' },
  { label: 'ING Fallback 5', section: 'Ingenierie', level: 5, isFallback: true, match: (e) => isIng(e) && e.niveau == 5, fallbackTo: 'IT 3' },
];

const SECTIONS_DEF = [
  {
    name: 'MASI',
    displayName: 'MASI',
    color: '#1E40AF',
    bgTotal: '#DBEAFE',
    textTotal: '#1E40AF',
    colorPart: '#7C3AED',
    bgTotalPart: '#F3E8FF',
    textTotalPart: '#6B21A8',
    rows: [
      'TC 1', 'TC2',
      'Finance 3', 'Marketing 3', 'Logistique 3', 'Sans Spécialité 3',
      'TC 4',
      'Finance 4', 'Marketing 4', 'Logistique 4', 'Sans Spécialité 4',
      'Finance 5', 'Marketing 5', 'Logistique 5', 'Sans Spécialité 5'
    ]
  },
  {
    name: 'IRM',
    displayName: 'IRM',
    color: '#D97706',
    bgTotal: '#FEF3C7',
    textTotal: '#92400E',
    colorPart: '#8B5CF6',
    bgTotalPart: '#F3E8FF',
    textTotalPart: '#6B21A8',
    rows: [
      'TC 1', 'TC2',
      'DEV 3', 'RCY 3', 'IA3', 'Sans Spécialité 3',
      'DEV 4', 'RCY 4', 'IA 4', 'Sans Spécialité 4',
      'DEV 5', 'RCY 5', 'IA 5', 'Sans Spécialité 5'
    ]
  },
  {
    name: 'LICENCE',
    displayName: 'LICENCE',
    color: '#0D9488',
    bgTotal: '#CCFBF1',
    textTotal: '#115E59',
    colorPart: '#7C3AED',
    bgTotalPart: '#F3E8FF',
    textTotalPart: '#6B21A8',
    rows: [
      'GC Licence 3',
      'Mecatronique Licence 3',
      'QHSE 3'
    ]
  },
  {
    name: 'MASTER',
    displayName: 'MASTER',
    color: '#7C3AED',
    bgTotal: '#EDE9FE',
    textTotal: '#5B21B6',
    colorPart: '#8B5CF6',
    bgTotalPart: '#F3E8FF',
    textTotalPart: '#6B21A8',
    rows: [
      'QHSE 4',
      'QHSE 5',
      'IEMS 4',
      'IEMS 5'
    ]
  },
  {
    name: 'Ingenierie',
    displayName: 'INGÉNIERIE',
    color: '#059669',
    bgTotal: '#D1FAE5',
    textTotal: '#065F46',
    colorPart: '#6D28D9',
    bgTotalPart: '#F3E8FF',
    textTotalPart: '#6B21A8',
    rows: [
      'CP 1', 'CP 2',
      'Mecatronique 1', 'Mecatronique 2', 'Mecatronique 3',
      'IT 1', 'IT 2', 'IT 3',
      'GC1', 'GC2', 'GC3'
    ]
  }
];

const BLOCKS_DEF = [
  {
    id: 'MAIN',
    title: 'SUP2I AÏN SEBAÂ',
    headerBg: '#0F172A',
    headerText: '#FFFFFF',
    totalLabel: 'TOTAL SUP2I AÏN SEBAÂ',
    totalBg: '#1E293B',
    totalText: '#FFFFFF',
    isPartner: false
  },
  {
    id: 'PART',
    title: 'SUP2I AÏN SEBAÂ (PARTENAIRE)',
    headerBg: '#581C87',
    headerText: '#FFFFFF',
    totalLabel: 'TOTAL SUP2I AÏN SEBAÂ (PARTENAIRE)',
    totalBg: '#4C1D95',
    totalText: '#FFFFFF',
    isPartner: true
  }
];

const formatMoney = (val) => {
  return (val || 0).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + ' DH';
};

const TdbPedagogiqueComponent = ({ anneeScolaire = '2026/2027', etudiants = [] }) => {
  const [loadingExport, setLoadingExport] = useState(false);
  const [masquerLignesVides, setMasquerLignesVides] = useState(false);
  const [filtreRecherche, setFiltreRecherche] = useState('');

  // Modal d'affichage de la liste des étudiants au clic sur un chiffre
  const [modalData, setModalData] = useState({
    isOpen: false,
    title: '',
    students: []
  });
  const [rechercheModal, setRechercheModal] = useState('');

  const openStudentsModal = (title, students = []) => {
    if (!students || students.length === 0) return;
    setRechercheModal('');
    setModalData({
      isOpen: true,
      title,
      students
    });
  };

  const closeStudentsModal = () => {
    setModalData({
      isOpen: false,
      title: '',
      students: []
    });
  };

  // Export Excel (.xlsx) de la liste filtrée dans le Modal
  const handleExportModalList = () => {
    const studentsToExport = modalStudentsFiltres && modalStudentsFiltres.length > 0
      ? modalStudentsFiltres
      : modalData.students;

    if (!studentsToExport || studentsToExport.length === 0) return;

    const data = studentsToExport.map((e, idx) => {
      const mode = (e.niveauFormation || 'FI').trim();
      const isNew = e.nouvelleInscription !== false ? 'Nouveau' : 'Réinscrit';
      const prix = (e.isPartner ? (parseFloat(e.prixTotalPartner) || parseFloat(e.prixTotal)) : parseFloat(e.prixTotal)) || 0;
      return {
        'N°': idx + 1,
        'Code Étudiant': e.codeEtudiant || '',
        'Nom': e.nomDeFamille || '',
        'Prénom': e.prenom || '',
        'Téléphone': e.telephone || '',
        'Email': e.email || '',
        'Filière': e.filiere || '',
        'Spécialité / Option': e.specialite || e.option || '',
        'Niveau': e.niveau || '',
        'Mode Formation': mode,
        'Statut Inscription': isNew,
        'Prix Validé (DH)': prix
      };
    });

    const ws = XLSX.utils.json_to_sheet(data);

    // Définir des largeurs de colonnes optimisées
    ws['!cols'] = [
      { wch: 6 },   // N°
      { wch: 18 },  // Code Étudiant
      { wch: 18 },  // Nom
      { wch: 18 },  // Prénom
      { wch: 16 },  // Téléphone
      { wch: 28 },  // Email
      { wch: 16 },  // Filière
      { wch: 26 },  // Spécialité / Option
      { wch: 10 },  // Niveau
      { wch: 16 },  // Mode Formation
      { wch: 18 },  // Statut Inscription
      { wch: 16 }   // Prix Validé
    ];

    const wb = XLSX.utils.book_new();
    const sheetName = (modalData.title || 'Étudiants').replace(/[:\\/?*[\]]/g, '_').substring(0, 31);
    XLSX.utils.book_append_sheet(wb, ws, sheetName || 'Étudiants');

    const cleanTitle = (modalData.title || 'Etudiants').replace(/[^a-zA-Z0-9_\-]/g, '_');
    const anneeClean = (anneeScolaire || '').replace(/[^a-zA-Z0-9_\-]/g, '-');
    XLSX.writeFile(wb, `Etudiants_${cleanTitle}_${anneeClean}.xlsx`);
  };

  // Calcul direct et réactif des données TDB avec stockage des listes d'étudiants
  const tdbData = useMemo(() => {
    const etudiantsFiltres = (anneeScolaire === 'toutes'
      ? etudiants
      : etudiants.filter(e => e.anneeScolaire === anneeScolaire)
    ).filter(e => {
      const isDesist = e.desistement?.statut === true || e.desistement?.statut === 'true';
      if (isDesist) return false;
      const isPart = e.isPartner === true || (e.partner && e.partner !== null) || (e.nomPartner && e.nomPartner !== null);
      if (isPart && e.lieuFormation !== 'Sup2i Aïn Sebaâ') return false;
      const price = (isPart ? (parseFloat(e.prixTotalPartner) || parseFloat(e.prixTotal)) : parseFloat(e.prixTotal)) || 0;
      if (price <= 0) return false;
      return true;
    });

    const dataMap = {};
    BLOCKS_DEF.forEach(block => {
      SECTIONS_DEF.forEach(sec => {
        sec.rows.forEach(label => {
          const key = `${block.id}_${sec.name}_${label}`;
          dataMap[key] = {
            label,
            section: sec.name,
            blockId: block.id,
            inscritsFI: 0, inscritsTA: 0, inscritsExec: 0, inscritsVAE: 0,
            reinscritsFI: 0, reinscritsTA: 0, reinscritsExec: 0, reinscritsVAE: 0,
            students_inscritsFI: [],
            students_inscritsTA: [],
            students_inscritsExec: [],
            students_inscritsVAE: [],
            students_reinscritsFI: [],
            students_reinscritsTA: [],
            students_reinscritsExec: [],
            students_reinscritsVAE: [],
            caTotal: 0
          };
        });
      });
    });

    etudiantsFiltres.forEach(e => {
      const isPart = e.isPartner === true || (e.partner && e.partner !== null) || (e.nomPartner && e.nomPartner !== null);
      const isSup2iPart = isPart && e.lieuFormation === 'Sup2i Aïn Sebaâ';

      let matchedLabel = null;
      let matchedSection = null;

      for (let def of TDB_TEMPLATE) {
        if (!def.isSection && def.match(e)) {
          matchedLabel = def.isFallback ? def.fallbackTo : def.label;
          matchedSection = def.section;
          break;
        }
      }

      if (matchedLabel && matchedSection) {
        const blockId = isSup2iPart ? 'PART' : 'MAIN';
        const key = `${blockId}_${matchedSection}_${matchedLabel}`;
        if (!dataMap[key]) {
          dataMap[key] = {
            label: matchedLabel,
            section: matchedSection,
            blockId,
            inscritsFI: 0, inscritsTA: 0, inscritsExec: 0, inscritsVAE: 0,
            reinscritsFI: 0, reinscritsTA: 0, reinscritsExec: 0, reinscritsVAE: 0,
            students_inscritsFI: [],
            students_inscritsTA: [],
            students_inscritsExec: [],
            students_inscritsVAE: [],
            students_reinscritsFI: [],
            students_reinscritsTA: [],
            students_reinscritsExec: [],
            students_reinscritsVAE: [],
            caTotal: 0
          };
        }

        const mode = (e.niveauFormation || 'FI').trim();
        const isNew = e.nouvelleInscription !== false;
        const price = (e.isPartner ? (parseFloat(e.prixTotalPartner) || parseFloat(e.prixTotal)) : parseFloat(e.prixTotal)) || 0;

        if (isNew) {
          if (mode === 'TA') {
            dataMap[key].inscritsTA++;
            dataMap[key].students_inscritsTA.push(e);
          } else if (mode === 'VAE') {
            dataMap[key].inscritsVAE++;
            dataMap[key].students_inscritsVAE.push(e);
          } else if (mode === 'Executive' || mode === 'Executive / VAE') {
            dataMap[key].inscritsExec++;
            dataMap[key].students_inscritsExec.push(e);
          } else {
            dataMap[key].inscritsFI++;
            dataMap[key].students_inscritsFI.push(e);
          }
        } else {
          if (mode === 'TA') {
            dataMap[key].reinscritsTA++;
            dataMap[key].students_reinscritsTA.push(e);
          } else if (mode === 'VAE') {
            dataMap[key].reinscritsVAE++;
            dataMap[key].students_reinscritsVAE.push(e);
          } else if (mode === 'Executive' || mode === 'Executive / VAE') {
            dataMap[key].reinscritsExec++;
            dataMap[key].students_reinscritsExec.push(e);
          } else {
            dataMap[key].reinscritsFI++;
            dataMap[key].students_reinscritsFI.push(e);
          }
        }

        dataMap[key].caTotal += price;
      }
    });

    let grandInsFI = 0, grandInsTA = 0, grandInsExec = 0, grandInsVAE = 0;
    let grandReinFI = 0, grandReinTA = 0, grandReinExec = 0, grandReinVAE = 0;
    let grandCA = 0;
    let grandStudentsInsFI = [];
    let grandStudentsInsTA = [];
    let grandStudentsInsExec = [];
    let grandStudentsInsVAE = [];
    let grandStudentsReinFI = [];
    let grandStudentsReinTA = [];
    let grandStudentsReinExec = [];
    let grandStudentsReinVAE = [];

    const blocks = BLOCKS_DEF.map(block => {
      let blockInsFI = 0, blockInsTA = 0, blockInsExec = 0, blockInsVAE = 0;
      let blockReinFI = 0, blockReinTA = 0, blockReinExec = 0, blockReinVAE = 0;
      let blockCA = 0;
      let blockStudentsInsFI = [];
      let blockStudentsInsTA = [];
      let blockStudentsInsExec = [];
      let blockStudentsInsVAE = [];
      let blockStudentsReinFI = [];
      let blockStudentsReinTA = [];
      let blockStudentsReinExec = [];
      let blockStudentsReinVAE = [];

      const sections = SECTIONS_DEF.map(sec => {
        let secInsFI = 0, secInsTA = 0, secInsExec = 0, secInsVAE = 0;
        let secReinFI = 0, secReinTA = 0, secReinExec = 0, secReinVAE = 0;
        let secCA = 0;
        let secStudentsInsFI = [];
        let secStudentsInsTA = [];
        let secStudentsInsExec = [];
        let secStudentsInsVAE = [];
        let secStudentsReinFI = [];
        let secStudentsReinTA = [];
        let secStudentsReinExec = [];
        let secStudentsReinVAE = [];

        const rows = sec.rows.map(label => {
          const key = `${block.id}_${sec.name}_${label}`;
          const item = dataMap[key] || {
            label,
            section: sec.name,
            blockId: block.id,
            inscritsFI: 0, inscritsTA: 0, inscritsExec: 0, inscritsVAE: 0,
            reinscritsFI: 0, reinscritsTA: 0, reinscritsExec: 0, reinscritsVAE: 0,
            students_inscritsFI: [],
            students_inscritsTA: [],
            students_inscritsExec: [],
            students_inscritsVAE: [],
            students_reinscritsFI: [],
            students_reinscritsTA: [],
            students_reinscritsExec: [],
            students_reinscritsVAE: [],
            caTotal: 0
          };

          secInsFI += item.inscritsFI;
          secInsTA += item.inscritsTA;
          secInsExec += item.inscritsExec;
          secInsVAE += item.inscritsVAE;
          secReinFI += item.reinscritsFI;
          secReinTA += item.reinscritsTA;
          secReinExec += item.reinscritsExec;
          secReinVAE += item.reinscritsVAE;
          secCA += item.caTotal;

          secStudentsInsFI.push(...(item.students_inscritsFI || []));
          secStudentsInsTA.push(...(item.students_inscritsTA || []));
          secStudentsInsExec.push(...(item.students_inscritsExec || []));
          secStudentsInsVAE.push(...(item.students_inscritsVAE || []));
          secStudentsReinFI.push(...(item.students_reinscritsFI || []));
          secStudentsReinTA.push(...(item.students_reinscritsTA || []));
          secStudentsReinExec.push(...(item.students_reinscritsExec || []));
          secStudentsReinVAE.push(...(item.students_reinscritsVAE || []));

          const totalInscrits = (item.inscritsFI || 0) + (item.inscritsTA || 0) + (item.inscritsExec || 0) + (item.inscritsVAE || 0);
          const totalReinscrits = (item.reinscritsFI || 0) + (item.reinscritsTA || 0) + (item.reinscritsExec || 0) + (item.reinscritsVAE || 0);

          const students_rowAll = [
            ...(item.students_inscritsFI || []),
            ...(item.students_inscritsTA || []),
            ...(item.students_inscritsExec || []),
            ...(item.students_inscritsVAE || []),
            ...(item.students_reinscritsFI || []),
            ...(item.students_reinscritsTA || []),
            ...(item.students_reinscritsExec || []),
            ...(item.students_reinscritsVAE || [])
          ];

          return {
            label,
            inscritsFI: item.inscritsFI || 0,
            inscritsTA: item.inscritsTA || 0,
            inscritsExec: item.inscritsExec || 0,
            inscritsVAE: item.inscritsVAE || 0,
            reinscritsFI: item.reinscritsFI || 0,
            reinscritsTA: item.reinscritsTA || 0,
            reinscritsExec: item.reinscritsExec || 0,
            reinscritsVAE: item.reinscritsVAE || 0,
            students_inscritsFI: item.students_inscritsFI || [],
            students_inscritsTA: item.students_inscritsTA || [],
            students_inscritsExec: item.students_inscritsExec || [],
            students_inscritsVAE: item.students_inscritsVAE || [],
            students_reinscritsFI: item.students_reinscritsFI || [],
            students_reinscritsTA: item.students_reinscritsTA || [],
            students_reinscritsExec: item.students_reinscritsExec || [],
            students_reinscritsVAE: item.students_reinscritsVAE || [],
            students_rowAll,
            caTotal: item.caTotal || 0,
            totalInscrits,
            totalReinscrits,
            totalEffectif: totalInscrits + totalReinscrits
          };
        });

        blockInsFI += secInsFI;
        blockInsTA += secInsTA;
        blockInsExec += secInsExec;
        blockInsVAE += secInsVAE;
        blockReinFI += secReinFI;
        blockReinTA += secReinTA;
        blockReinExec += secReinExec;
        blockReinVAE += secReinVAE;
        blockCA += secCA;

        blockStudentsInsFI.push(...secStudentsInsFI);
        blockStudentsInsTA.push(...secStudentsInsTA);
        blockStudentsInsExec.push(...secStudentsInsExec);
        blockStudentsInsVAE.push(...secStudentsInsVAE);
        blockStudentsReinFI.push(...secStudentsReinFI);
        blockStudentsReinTA.push(...secStudentsReinTA);
        blockStudentsReinExec.push(...secStudentsReinExec);
        blockStudentsReinVAE.push(...secStudentsReinVAE);

        const totalInscrits = secInsFI + secInsTA + secInsExec + secInsVAE;
        const totalReinscrits = secReinFI + secReinTA + secReinExec + secReinVAE;
        const secStudentsAll = [
          ...secStudentsInsFI, ...secStudentsInsTA, ...secStudentsInsExec, ...secStudentsInsVAE,
          ...secStudentsReinFI, ...secStudentsReinTA, ...secStudentsReinExec, ...secStudentsReinVAE
        ];

        return {
          name: sec.name,
          displayName: sec.displayName || sec.name,
          color: block.isPartner ? sec.colorPart : sec.color,
          bgTotal: block.isPartner ? sec.bgTotalPart : sec.bgTotal,
          textTotal: block.isPartner ? sec.textTotalPart : sec.textTotal,
          rows,
          subtotal: {
            inscritsFI: secInsFI,
            inscritsTA: secInsTA,
            inscritsExec: secInsExec,
            inscritsVAE: secInsVAE,
            reinscritsFI: secReinFI,
            reinscritsTA: secReinTA,
            reinscritsExec: secReinExec,
            reinscritsVAE: secReinVAE,
            students_inscritsFI: secStudentsInsFI,
            students_inscritsTA: secStudentsInsTA,
            students_inscritsExec: secStudentsInsExec,
            students_inscritsVAE: secStudentsInsVAE,
            students_reinscritsFI: secStudentsReinFI,
            students_reinscritsTA: secStudentsReinTA,
            students_reinscritsExec: secStudentsReinExec,
            students_reinscritsVAE: secStudentsReinVAE,
            students_all: secStudentsAll,
            totalInscrits,
            totalReinscrits,
            totalEffectif: totalInscrits + totalReinscrits,
            caTotal: secCA
          }
        };
      });

      grandInsFI += blockInsFI;
      grandInsTA += blockInsTA;
      grandInsExec += blockInsExec;
      grandInsVAE += blockInsVAE;
      grandReinFI += blockReinFI;
      grandReinTA += blockReinTA;
      grandReinExec += blockReinExec;
      grandReinVAE += blockReinVAE;
      grandCA += blockCA;

      grandStudentsInsFI.push(...blockStudentsInsFI);
      grandStudentsInsTA.push(...blockStudentsInsTA);
      grandStudentsInsExec.push(...blockStudentsInsExec);
      grandStudentsInsVAE.push(...blockStudentsInsVAE);
      grandStudentsReinFI.push(...blockStudentsReinFI);
      grandStudentsReinTA.push(...blockStudentsReinTA);
      grandStudentsReinExec.push(...blockStudentsReinExec);
      grandStudentsReinVAE.push(...blockStudentsReinVAE);

      const totalInscrits = blockInsFI + blockInsTA + blockInsExec + blockInsVAE;
      const totalReinscrits = blockReinFI + blockReinTA + blockReinExec + blockReinVAE;
      const blockStudentsAll = [
        ...blockStudentsInsFI, ...blockStudentsInsTA, ...blockStudentsInsExec, ...blockStudentsInsVAE,
        ...blockStudentsReinFI, ...blockStudentsReinTA, ...blockStudentsReinExec, ...blockStudentsReinVAE
      ];

      return {
        id: block.id,
        title: block.title,
        totalLabel: block.totalLabel,
        headerBg: block.headerBg,
        headerText: block.headerText,
        totalBg: block.totalBg,
        totalText: block.totalText,
        isPartner: block.isPartner,
        sections,
        total: {
          inscritsFI: blockInsFI,
          inscritsTA: blockInsTA,
          inscritsExec: blockInsExec,
          inscritsVAE: blockInsVAE,
          reinscritsFI: blockReinFI,
          reinscritsTA: blockReinTA,
          reinscritsExec: blockReinExec,
          reinscritsVAE: blockReinVAE,
          students_inscritsFI: blockStudentsInsFI,
          students_inscritsTA: blockStudentsInsTA,
          students_inscritsExec: blockStudentsInsExec,
          students_inscritsVAE: blockStudentsInsVAE,
          students_reinscritsFI: blockStudentsReinFI,
          students_reinscritsTA: blockStudentsReinTA,
          students_reinscritsExec: blockStudentsReinExec,
          students_reinscritsVAE: blockStudentsReinVAE,
          students_all: blockStudentsAll,
          totalInscrits,
          totalReinscrits,
          totalEffectif: totalInscrits + totalReinscrits,
          caTotal: blockCA
        }
      };
    });

    const totalInscrits = grandInsFI + grandInsTA + grandInsExec + grandInsVAE;
    const totalReinscrits = grandReinFI + grandReinTA + grandReinExec + grandReinVAE;
    const grandStudentsAllInscrits = [
      ...grandStudentsInsFI, ...grandStudentsInsTA, ...grandStudentsInsExec, ...grandStudentsInsVAE
    ];
    const grandStudentsAllReinscrits = [
      ...grandStudentsReinFI, ...grandStudentsReinTA, ...grandStudentsReinExec, ...grandStudentsReinVAE
    ];
    const grandStudentsTotal = [
      ...grandStudentsAllInscrits, ...grandStudentsAllReinscrits
    ];

    const grandTotal = {
      inscritsFI: grandInsFI,
      inscritsTA: grandInsTA,
      inscritsExec: grandInsExec,
      inscritsVAE: grandInsVAE,
      reinscritsFI: grandReinFI,
      reinscritsTA: grandReinTA,
      reinscritsExec: grandReinExec,
      reinscritsVAE: grandReinVAE,
      students_inscritsFI: grandStudentsInsFI,
      students_inscritsTA: grandStudentsInsTA,
      students_inscritsExec: grandStudentsInsExec,
      students_inscritsVAE: grandStudentsInsVAE,
      students_reinscritsFI: grandStudentsReinFI,
      students_reinscritsTA: grandStudentsReinTA,
      students_reinscritsExec: grandStudentsReinExec,
      students_reinscritsVAE: grandStudentsReinVAE,
      students_allInscrits: grandStudentsAllInscrits,
      students_allReinscrits: grandStudentsAllReinscrits,
      students_total: grandStudentsTotal,
      totalInscrits,
      totalReinscrits,
      totalEffectif: totalInscrits + totalReinscrits,
      caTotal: grandCA
    };

    return {
      totalEtudiantsAnalyses: etudiantsFiltres.length,
      blocks,
      grandTotal
    };
  }, [anneeScolaire, etudiants]);

  // Export Excel identique au bouton "TDB Pédagogique"
  const handleExportExcel = async () => {
    try {
      setLoadingExport(true);
      const token = localStorage.getItem('token');
      const baseUrl = (API_URL || ' https://vmi1977988.contaboserver.net').trim();
      const res = await fetch(`${baseUrl}/api2/etudiants/export-tdb-pedagogie`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!res.ok) {
        throw new Error(`Erreur HTTP: ${res.status}`);
      }

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `TDB_Pedagogie_Extraction_ENT_${anneeScolaire.replace('/', '-')}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
      setLoadingExport(false);
    } catch (err) {
      console.error('Erreur export TDB:', err);
      alert('Erreur lors du téléchargement Excel : ' + err.message);
      setLoadingExport(false);
    }
  };

  // Rendu interactif d'une cellule numérique avec clic ouvrant le modal
  const renderCellNumber = (val, title, students = []) => {
    if (!val || val === 0) {
      return <span style={{ color: '#cbd5e1', fontWeight: 'normal', fontSize: '0.9rem' }}>0</span>;
    }
    return (
      <button
        type="button"
        onClick={() => openStudentsModal(title, students)}
        title={`Cliquer pour voir la liste des ${val} étudiant(s)`}
        style={{
          color: '#0369a1',
          fontWeight: '800',
          fontSize: '0.95rem',
          display: 'inline-block',
          minWidth: '28px',
          padding: '2px 8px',
          background: '#f0f9ff',
          borderRadius: '5px',
          border: '1px solid #bae6fd',
          cursor: 'pointer',
          outline: 'none',
          transition: 'all 0.15s ease',
          boxShadow: '0 1px 2px rgba(2, 132, 199, 0.08)'
        }}
        onMouseOver={(e) => {
          e.currentTarget.style.background = '#0284c7';
          e.currentTarget.style.color = '#ffffff';
          e.currentTarget.style.borderColor = '#0284c7';
          e.currentTarget.style.transform = 'scale(1.08)';
          e.currentTarget.style.boxShadow = '0 3px 8px rgba(2, 132, 199, 0.35)';
        }}
        onMouseOut={(e) => {
          e.currentTarget.style.background = '#f0f9ff';
          e.currentTarget.style.color = '#0369a1';
          e.currentTarget.style.borderColor = '#bae6fd';
          e.currentTarget.style.transform = 'scale(1)';
          e.currentTarget.style.boxShadow = '0 1px 2px rgba(2, 132, 199, 0.08)';
        }}
      >
        {val}
      </button>
    );
  };

  // Filtrage des étudiants dans le modal
  const modalStudentsFiltres = useMemo(() => {
    if (!modalData.students) return [];
    if (!rechercheModal.trim()) return modalData.students;
    const q = rechercheModal.toLowerCase();
    return modalData.students.filter(e => {
      const nom = `${e.nomDeFamille || ''} ${e.prenom || ''}`.toLowerCase();
      const code = (e.codeEtudiant || '').toLowerCase();
      const tel = (e.telephone || '').toLowerCase();
      const email = (e.email || '').toLowerCase();
      const filiere = (e.filiere || '').toLowerCase();
      const specialite = (e.specialite || e.option || '').toLowerCase();
      return nom.includes(q) || code.includes(q) || tel.includes(q) || email.includes(q) || filiere.includes(q) || specialite.includes(q);
    });
  }, [modalData.students, rechercheModal]);

  return (
    <div style={{
      background: '#ffffff',
      borderRadius: '12px',
      padding: '1.75rem',
      marginBottom: '2rem',
      border: '1px solid #e2e8f0',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)'
    }}>
      {/* En-tête avec Titre & Bouton Exporter */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '1rem',
        paddingBottom: '1.25rem',
        borderBottom: '2px solid #e2e8f0',
        marginBottom: '1.5rem'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.85rem' }}>
          <div style={{
            background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
            padding: '0.7rem',
            borderRadius: '10px',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <FileSpreadsheet size={26} />
          </div>
          <div>
            <h2 style={{
              fontSize: '1.35rem',
              fontWeight: '800',
              color: '#0f172a',
              margin: 0,
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              Tableau de Bord Pédagogique (TDB)
              <span style={{
                background: '#eff6ff',
                color: '#1d4ed8',
                fontSize: '0.85rem',
                padding: '0.25rem 0.65rem',
                borderRadius: '9999px',
                border: '1px solid #bfdbfe'
              }}>
                {anneeScolaire}
              </span>
            </h2>
            <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.85rem', color: '#64748b' }}>
              Extraction pédagogique officielle — Répartition par filière, option, mode et statut d'inscription
            </p>
          </div>
        </div>

        {/* Actions : Exporter Excel & Filtres */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
          <button
            onClick={() => setMasquerLignesVides(!masquerLignesVides)}
            style={{
              padding: '0.55rem 0.95rem',
              background: masquerLignesVides ? '#f1f5f9' : '#fff',
              color: masquerLignesVides ? '#0f172a' : '#64748b',
              border: '1px solid #cbd5e1',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: '600',
              fontSize: '0.85rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.4rem',
              transition: 'all 0.2s ease'
            }}
            title="Masquer les options ayant 0 étudiant"
          >
            {masquerLignesVides ? <EyeOff size={16} /> : <Eye size={16} />}
            {masquerLignesVides ? 'Lignes vides masquées' : 'Toutes les lignes'}
          </button>

          <button
            onClick={handleExportExcel}
            disabled={loadingExport}
            style={{
              padding: '0.6rem 1.25rem',
              background: 'linear-gradient(135deg, #15803d 0%, #16a34a 100%)',
              color: '#ffffff',
              border: 'none',
              borderRadius: '8px',
              cursor: loadingExport ? 'not-allowed' : 'pointer',
              fontWeight: '700',
              fontSize: '0.9rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              boxShadow: '0 2px 4px rgba(22, 163, 74, 0.3)',
              transition: 'all 0.2s ease',
              opacity: loadingExport ? 0.7 : 1
            }}
          >
            <Download size={18} />
            {loadingExport ? 'Génération en cours...' : '📊 Exporter Excel'}
          </button>
        </div>
      </div>

      {/* Cartes KPI récapitulatives interactives */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))',
        gap: '1rem',
        marginBottom: '1.75rem'
      }}>
        <div
          onClick={() => openStudentsModal('Tous les Étudiants du TDB Pédagogique', tdbData.grandTotal.students_total)}
          style={{
            background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
            padding: '1rem 1.25rem',
            borderRadius: '10px',
            border: '1px solid #e2e8f0',
            cursor: 'pointer',
            transition: 'transform 0.15s ease, box-shadow 0.15s ease'
          }}
          onMouseOver={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 12px rgba(0,0,0,0.06)'; }}
          onMouseOut={(e) => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none'; }}
          title="Cliquer pour afficher la liste de tous les étudiants"
        >
          <span style={{ fontSize: '0.8rem', color: '#64748b', fontWeight: '600', textTransform: 'uppercase' }}>
            Effectif Total TDB 🔍
          </span>
          <div style={{ fontSize: '1.75rem', fontWeight: '800', color: '#0f172a', marginTop: '0.2rem' }}>
            {tdbData.grandTotal.totalEffectif}
          </div>
          <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
            Étudiants actifs avec prix validé
          </span>
        </div>

        <div
          onClick={() => openStudentsModal('Nouveaux Inscrits (TDB)', tdbData.grandTotal.students_allInscrits)}
          style={{
            background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
            padding: '1rem 1.25rem',
            borderRadius: '10px',
            border: '1px solid #bfdbfe',
            cursor: 'pointer',
            transition: 'transform 0.15s ease, box-shadow 0.15s ease'
          }}
          onMouseOver={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 12px rgba(30,58,138,0.12)'; }}
          onMouseOut={(e) => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none'; }}
          title="Cliquer pour afficher tous les nouveaux inscrits"
        >
          <span style={{ fontSize: '0.8rem', color: '#1d4ed8', fontWeight: '600', textTransform: 'uppercase' }}>
            Nouveaux Inscrits 🔍
          </span>
          <div style={{ fontSize: '1.75rem', fontWeight: '800', color: '#1e40af', marginTop: '0.2rem' }}>
            {tdbData.grandTotal.totalInscrits}
          </div>
          <span style={{ fontSize: '0.75rem', color: '#3b82f6' }}>
            FI: {tdbData.grandTotal.inscritsFI} | TA: {tdbData.grandTotal.inscritsTA} | Exec: {tdbData.grandTotal.inscritsExec} | VAE: {tdbData.grandTotal.inscritsVAE}
          </span>
        </div>

        <div
          onClick={() => openStudentsModal('Réinscriptions (TDB)', tdbData.grandTotal.students_allReinscrits)}
          style={{
            background: 'linear-gradient(135deg, #faf5ff 0%, #f3e8ff 100%)',
            padding: '1rem 1.25rem',
            borderRadius: '10px',
            border: '1px solid #e9d5ff',
            cursor: 'pointer',
            transition: 'transform 0.15s ease, box-shadow 0.15s ease'
          }}
          onMouseOver={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 12px rgba(126,34,206,0.12)'; }}
          onMouseOut={(e) => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none'; }}
          title="Cliquer pour afficher toutes les réinscriptions"
        >
          <span style={{ fontSize: '0.8rem', color: '#7e22ce', fontWeight: '600', textTransform: 'uppercase' }}>
            Réinscriptions 🔍
          </span>
          <div style={{ fontSize: '1.75rem', fontWeight: '800', color: '#6b21a8', marginTop: '0.2rem' }}>
            {tdbData.grandTotal.totalReinscrits}
          </div>
          <span style={{ fontSize: '0.75rem', color: '#9333ea' }}>
            FI: {tdbData.grandTotal.reinscritsFI} | TA: {tdbData.grandTotal.reinscritsTA} | Exec: {tdbData.grandTotal.reinscritsExec} | VAE: {tdbData.grandTotal.reinscritsVAE}
          </span>
        </div>

        <div style={{
          background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
          padding: '1rem 1.25rem',
          borderRadius: '10px',
          border: '1px solid #bbf7d0'
        }}>
          <span style={{ fontSize: '0.8rem', color: '#15803d', fontWeight: '600', textTransform: 'uppercase' }}>
            Chiffre d'Affaires Total
          </span>
          <div style={{ fontSize: '1.45rem', fontWeight: '800', color: '#166534', marginTop: '0.2rem' }}>
            {formatMoney(tdbData.grandTotal.caTotal)}
          </div>
          <span style={{ fontSize: '0.75rem', color: '#16a34a' }}>
            Total général validé
          </span>
        </div>
      </div>

      {/* Barre de recherche d'option */}
      <div style={{
        marginBottom: '1rem',
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        maxWidth: '350px'
      }}>
        <div style={{
          position: 'relative',
          width: '100%'
        }}>
          <Search size={16} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
          <input
            type="text"
            value={filtreRecherche}
            onChange={(e) => setFiltreRecherche(e.target.value)}
            placeholder="Filtrer une filière ou option..."
            style={{
              width: '100%',
              padding: '0.45rem 0.75rem 0.45rem 2.1rem',
              borderRadius: '6px',
              border: '1px solid #cbd5e1',
              fontSize: '0.85rem',
              outline: 'none'
            }}
          />
        </div>
        {filtreRecherche && (
          <button
            onClick={() => setFiltreRecherche('')}
            style={{
              border: 'none',
              background: '#f1f5f9',
              borderRadius: '4px',
              padding: '0.45rem 0.65rem',
              cursor: 'pointer',
              fontSize: '0.8rem',
              color: '#64748b'
            }}
          >
            Effacer
          </button>
        )}
      </div>

      {/* Table TDB Pédagogique - Structure fidèle au fichier Excel */}
      <div style={{
        overflowX: 'auto',
        borderRadius: '8px',
        border: '1px solid #cbd5e1',
        background: '#ffffff',
        boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.03)'
      }}>
        <table style={{
          width: '100%',
          minWidth: '1280px',
          borderCollapse: 'collapse',
          fontSize: '0.9rem',
          fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"
        }}>
          <thead>
            {/* Ligne 1 : En-tête Supérieur */}
            <tr style={{ background: '#1e3a8a', color: '#ffffff' }}>
              <th
                rowSpan={2}
                style={{
                  background: '#0f172a',
                  color: '#ffffff',
                  padding: '0.85rem 1.25rem',
                  textAlign: 'left',
                  fontWeight: '700',
                  fontSize: '0.95rem',
                  border: '1px solid #334155',
                  minWidth: '240px',
                  width: '24%',
                  verticalAlign: 'middle'
                }}
              >
                Filière / Option
              </th>
              <th
                colSpan={4}
                style={{
                  background: '#1e3a8a',
                  color: '#ffffff',
                  padding: '0.75rem 0.5rem',
                  textAlign: 'center',
                  fontWeight: '800',
                  fontSize: '1rem',
                  letterSpacing: '0.06em',
                  border: '1px solid #3b82f6',
                  width: '33%'
                }}
              >
                INSCRITS
              </th>
              <th
                colSpan={4}
                style={{
                  background: '#1e3a8a',
                  color: '#ffffff',
                  padding: '0.75rem 0.5rem',
                  textAlign: 'center',
                  fontWeight: '800',
                  fontSize: '1rem',
                  letterSpacing: '0.06em',
                  border: '1px solid #3b82f6',
                  width: '33%'
                }}
              >
                REINSCRITS
              </th>
              <th
                rowSpan={2}
                style={{
                  background: '#1d4ed8',
                  color: '#ffffff',
                  padding: '0.85rem 1.25rem',
                  textAlign: 'right',
                  fontWeight: '700',
                  fontSize: '0.95rem',
                  border: '1px solid #2563eb',
                  minWidth: '160px',
                  width: '10%',
                  verticalAlign: 'middle'
                }}
              >
                Total CA (DH)
              </th>
            </tr>

            {/* Ligne 2 : Sous-colonnes */}
            <tr style={{ background: '#2563eb', color: '#ffffff' }}>
              {/* Inscrits sub-columns */}
              <th style={{ padding: '0.65rem 0.4rem', textAlign: 'center', fontWeight: '700', fontSize: '0.85rem', border: '1px solid #3b82f6', minWidth: '75px' }}>FI</th>
              <th style={{ padding: '0.65rem 0.4rem', textAlign: 'center', fontWeight: '700', fontSize: '0.85rem', border: '1px solid #3b82f6', minWidth: '75px' }}>TA</th>
              <th style={{ padding: '0.65rem 0.4rem', textAlign: 'center', fontWeight: '700', fontSize: '0.85rem', border: '1px solid #3b82f6', minWidth: '105px' }}>Executive</th>
              <th style={{ padding: '0.65rem 0.4rem', textAlign: 'center', fontWeight: '700', fontSize: '0.85rem', border: '1px solid #3b82f6', minWidth: '75px' }}>VAE</th>
              {/* Réinscrits sub-columns */}
              <th style={{ padding: '0.65rem 0.4rem', textAlign: 'center', fontWeight: '700', fontSize: '0.85rem', border: '1px solid #3b82f6', minWidth: '75px' }}>FI</th>
              <th style={{ padding: '0.65rem 0.4rem', textAlign: 'center', fontWeight: '700', fontSize: '0.85rem', border: '1px solid #3b82f6', minWidth: '75px' }}>TA</th>
              <th style={{ padding: '0.65rem 0.4rem', textAlign: 'center', fontWeight: '700', fontSize: '0.85rem', border: '1px solid #3b82f6', minWidth: '105px' }}>Executive</th>
              <th style={{ padding: '0.65rem 0.4rem', textAlign: 'center', fontWeight: '700', fontSize: '0.85rem', border: '1px solid #3b82f6', minWidth: '75px' }}>VAE</th>
            </tr>
          </thead>

          <tbody>
            {tdbData.blocks.map(block => {
              const sectionsFiltered = block.sections.map(sec => {
                const rowsFiltered = sec.rows.filter(row => {
                  if (filtreRecherche && !row.label.toLowerCase().includes(filtreRecherche.toLowerCase())) {
                    return false;
                  }
                  if (masquerLignesVides && row.totalEffectif === 0) {
                    return false;
                  }
                  return true;
                });
                return { ...sec, rowsFiltered };
              });

              const blockTotalEffectif = block.total.totalEffectif;
              const hasVisibleRows = sectionsFiltered.some(s => s.rowsFiltered.length > 0);

              if (!hasVisibleRows && (filtreRecherche || (masquerLignesVides && blockTotalEffectif === 0))) {
                return null;
              }

              return (
                <React.Fragment key={block.id}>
                  {/* Bannière du Bloc (ex: SUP2I AÏN SEBAÂ) */}
                  <tr>
                    <td
                      colSpan={10}
                      style={{
                        background: block.headerBg,
                        color: block.headerText,
                        padding: '0.8rem 1.25rem',
                        fontWeight: '800',
                        fontSize: '1rem',
                        letterSpacing: '0.05em',
                        border: '1px solid #334155'
                      }}
                    >
                      {block.title}
                    </td>
                  </tr>

                  {/* Sections du Bloc (MASI, IRM, INGÉNIERIE) */}
                  {sectionsFiltered.map(sec => {
                    if (sec.rowsFiltered.length === 0 && (filtreRecherche || (masquerLignesVides && sec.subtotal.totalEffectif === 0))) {
                      return null;
                    }

                    return (
                      <React.Fragment key={`${block.id}_${sec.name}`}>
                        {/* En-tête de section */}
                        <tr>
                          <td
                            colSpan={10}
                            style={{
                              background: sec.color,
                              color: '#ffffff',
                              padding: '0.65rem 1.25rem',
                              fontWeight: '700',
                              fontSize: '0.9rem',
                              letterSpacing: '0.04em',
                              border: '1px solid #cbd5e1'
                            }}
                          >
                            {sec.displayName}
                          </td>
                        </tr>

                        {/* Données de chaque option */}
                        {sec.rowsFiltered.map((row, idx) => {
                          const isEven = idx % 2 === 0;
                          return (
                            <tr
                              key={`${block.id}_${sec.name}_${row.label}`}
                              style={{
                                background: isEven ? '#f8fafc' : '#ffffff',
                                borderBottom: '1px solid #e2e8f0',
                                transition: 'background-color 0.15s ease'
                              }}
                              onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f1f5f9'}
                              onMouseOut={(e) => e.currentTarget.style.backgroundColor = isEven ? '#f8fafc' : '#ffffff'}
                            >
                              <td style={{
                                padding: '0.65rem 1.25rem',
                                fontWeight: '600',
                                color: '#0f172a',
                                borderRight: '1px solid #e2e8f0',
                                borderLeft: '1px solid #e2e8f0'
                              }}>
                                {row.label}
                              </td>

                              {/* Inscrits */}
                              <td style={{ textAlign: 'center', padding: '0.65rem 0.4rem', borderRight: '1px solid #e2e8f0' }}>
                                {renderCellNumber(row.inscritsFI, `${sec.displayName} : ${row.label} (Inscrits FI)`, row.students_inscritsFI)}
                              </td>
                              <td style={{ textAlign: 'center', padding: '0.65rem 0.4rem', borderRight: '1px solid #e2e8f0' }}>
                                {renderCellNumber(row.inscritsTA, `${sec.displayName} : ${row.label} (Inscrits TA)`, row.students_inscritsTA)}
                              </td>
                              <td style={{ textAlign: 'center', padding: '0.65rem 0.4rem', borderRight: '1px solid #e2e8f0' }}>
                                {renderCellNumber(row.inscritsExec, `${sec.displayName} : ${row.label} (Inscrits Executive)`, row.students_inscritsExec)}
                              </td>
                              <td style={{ textAlign: 'center', padding: '0.65rem 0.4rem', borderRight: '1px solid #e2e8f0' }}>
                                {renderCellNumber(row.inscritsVAE, `${sec.displayName} : ${row.label} (Inscrits VAE)`, row.students_inscritsVAE)}
                              </td>

                              {/* Réinscrits */}
                              <td style={{ textAlign: 'center', padding: '0.65rem 0.4rem', borderRight: '1px solid #e2e8f0' }}>
                                {renderCellNumber(row.reinscritsFI, `${sec.displayName} : ${row.label} (Réinscrits FI)`, row.students_reinscritsFI)}
                              </td>
                              <td style={{ textAlign: 'center', padding: '0.65rem 0.4rem', borderRight: '1px solid #e2e8f0' }}>
                                {renderCellNumber(row.reinscritsTA, `${sec.displayName} : ${row.label} (Réinscrits TA)`, row.students_reinscritsTA)}
                              </td>
                              <td style={{ textAlign: 'center', padding: '0.65rem 0.4rem', borderRight: '1px solid #e2e8f0' }}>
                                {renderCellNumber(row.reinscritsExec, `${sec.displayName} : ${row.label} (Réinscrits Executive)`, row.students_reinscritsExec)}
                              </td>
                              <td style={{ textAlign: 'center', padding: '0.65rem 0.4rem', borderRight: '1px solid #e2e8f0' }}>
                                {renderCellNumber(row.reinscritsVAE, `${sec.displayName} : ${row.label} (Réinscrits VAE)`, row.students_reinscritsVAE)}
                              </td>

                              {/* CA */}
                              <td style={{
                                textAlign: 'right',
                                padding: '0.65rem 1.25rem',
                                fontWeight: '700',
                                fontSize: '0.92rem',
                                color: row.caTotal > 0 ? '#0f172a' : '#94a3b8',
                                borderRight: '1px solid #e2e8f0'
                              }}>
                                {row.caTotal > 0 ? formatMoney(row.caTotal) : '0 DH'}
                              </td>
                            </tr>
                          );
                        })}

                        {/* Sous-total Section */}
                        <tr style={{
                          background: sec.bgTotal,
                          color: sec.textTotal,
                          fontWeight: '800',
                          borderTop: '1px solid #cbd5e1',
                          borderBottom: '1px solid #cbd5e1'
                        }}>
                          <td
                            onClick={() => openStudentsModal(`Total ${sec.displayName}`, sec.subtotal.students_all)}
                            style={{
                              padding: '0.7rem 1.25rem',
                              borderRight: '1px solid #cbd5e1',
                              borderLeft: '1px solid #cbd5e1',
                              fontSize: '0.92rem',
                              cursor: 'pointer'
                            }}
                            title="Cliquer pour voir tous les étudiants de cette section"
                          >
                            TOTAL {sec.displayName} 🔍
                          </td>
                          <td style={{ textAlign: 'center', padding: '0.7rem 0.4rem', borderRight: '1px solid #cbd5e1', fontSize: '0.95rem' }}>
                            {renderCellNumber(sec.subtotal.inscritsFI, `Total ${sec.displayName} (Inscrits FI)`, sec.subtotal.students_inscritsFI)}
                          </td>
                          <td style={{ textAlign: 'center', padding: '0.7rem 0.4rem', borderRight: '1px solid #cbd5e1', fontSize: '0.95rem' }}>
                            {renderCellNumber(sec.subtotal.inscritsTA, `Total ${sec.displayName} (Inscrits TA)`, sec.subtotal.students_inscritsTA)}
                          </td>
                          <td style={{ textAlign: 'center', padding: '0.7rem 0.4rem', borderRight: '1px solid #cbd5e1', fontSize: '0.95rem' }}>
                            {renderCellNumber(sec.subtotal.inscritsExec, `Total ${sec.displayName} (Inscrits Executive)`, sec.subtotal.students_inscritsExec)}
                          </td>
                          <td style={{ textAlign: 'center', padding: '0.7rem 0.4rem', borderRight: '1px solid #cbd5e1', fontSize: '0.95rem' }}>
                            {renderCellNumber(sec.subtotal.inscritsVAE, `Total ${sec.displayName} (Inscrits VAE)`, sec.subtotal.students_inscritsVAE)}
                          </td>
                          <td style={{ textAlign: 'center', padding: '0.7rem 0.4rem', borderRight: '1px solid #cbd5e1', fontSize: '0.95rem' }}>
                            {renderCellNumber(sec.subtotal.reinscritsFI, `Total ${sec.displayName} (Réinscrits FI)`, sec.subtotal.students_reinscritsFI)}
                          </td>
                          <td style={{ textAlign: 'center', padding: '0.7rem 0.4rem', borderRight: '1px solid #cbd5e1', fontSize: '0.95rem' }}>
                            {renderCellNumber(sec.subtotal.reinscritsTA, `Total ${sec.displayName} (Réinscrits TA)`, sec.subtotal.students_reinscritsTA)}
                          </td>
                          <td style={{ textAlign: 'center', padding: '0.7rem 0.4rem', borderRight: '1px solid #cbd5e1', fontSize: '0.95rem' }}>
                            {renderCellNumber(sec.subtotal.reinscritsExec, `Total ${sec.displayName} (Réinscrits Executive)`, sec.subtotal.students_reinscritsExec)}
                          </td>
                          <td style={{ textAlign: 'center', padding: '0.7rem 0.4rem', borderRight: '1px solid #cbd5e1', fontSize: '0.95rem' }}>
                            {renderCellNumber(sec.subtotal.reinscritsVAE, `Total ${sec.displayName} (Réinscrits VAE)`, sec.subtotal.students_reinscritsVAE)}
                          </td>
                          <td style={{ textAlign: 'right', padding: '0.7rem 1.25rem', borderRight: '1px solid #cbd5e1', fontSize: '0.95rem' }}>
                            {formatMoney(sec.subtotal.caTotal)}
                          </td>
                        </tr>
                      </React.Fragment>
                    );
                  })}

                  {/* Total du Bloc */}
                  <tr style={{
                    background: block.totalBg,
                    color: block.totalText,
                    fontWeight: '800',
                    borderTop: '2px solid #0f172a',
                    borderBottom: '2px solid #0f172a'
                  }}>
                    <td
                      onClick={() => openStudentsModal(block.totalLabel, block.total.students_all)}
                      style={{
                        padding: '0.8rem 1.25rem',
                        borderRight: '1px solid #475569',
                        borderLeft: '1px solid #475569',
                        letterSpacing: '0.03em',
                        fontSize: '0.95rem',
                        cursor: 'pointer'
                      }}
                      title="Cliquer pour voir tous les étudiants de ce bloc"
                    >
                      {block.totalLabel} 🔍
                    </td>
                    <td style={{ textAlign: 'center', padding: '0.8rem 0.4rem', borderRight: '1px solid #475569', fontSize: '1rem' }}>
                      {renderCellNumber(block.total.inscritsFI, `${block.totalLabel} (Inscrits FI)`, block.total.students_inscritsFI)}
                    </td>
                    <td style={{ textAlign: 'center', padding: '0.8rem 0.4rem', borderRight: '1px solid #475569', fontSize: '1rem' }}>
                      {renderCellNumber(block.total.inscritsTA, `${block.totalLabel} (Inscrits TA)`, block.total.students_inscritsTA)}
                    </td>
                    <td style={{ textAlign: 'center', padding: '0.8rem 0.4rem', borderRight: '1px solid #475569', fontSize: '1rem' }}>
                      {renderCellNumber(block.total.inscritsExec, `${block.totalLabel} (Inscrits Executive)`, block.total.students_inscritsExec)}
                    </td>
                    <td style={{ textAlign: 'center', padding: '0.8rem 0.4rem', borderRight: '1px solid #475569', fontSize: '1rem' }}>
                      {renderCellNumber(block.total.inscritsVAE, `${block.totalLabel} (Inscrits VAE)`, block.total.students_inscritsVAE)}
                    </td>
                    <td style={{ textAlign: 'center', padding: '0.8rem 0.4rem', borderRight: '1px solid #475569', fontSize: '1rem' }}>
                      {renderCellNumber(block.total.reinscritsFI, `${block.totalLabel} (Réinscrits FI)`, block.total.students_reinscritsFI)}
                    </td>
                    <td style={{ textAlign: 'center', padding: '0.8rem 0.4rem', borderRight: '1px solid #475569', fontSize: '1rem' }}>
                      {renderCellNumber(block.total.reinscritsTA, `${block.totalLabel} (Réinscrits TA)`, block.total.students_reinscritsTA)}
                    </td>
                    <td style={{ textAlign: 'center', padding: '0.8rem 0.4rem', borderRight: '1px solid #475569', fontSize: '1rem' }}>
                      {renderCellNumber(block.total.reinscritsExec, `${block.totalLabel} (Réinscrits Executive)`, block.total.students_reinscritsExec)}
                    </td>
                    <td style={{ textAlign: 'center', padding: '0.8rem 0.4rem', borderRight: '1px solid #475569', fontSize: '1rem' }}>
                      {renderCellNumber(block.total.reinscritsVAE, `${block.totalLabel} (Réinscrits VAE)`, block.total.students_reinscritsVAE)}
                    </td>
                    <td style={{ textAlign: 'right', padding: '0.8rem 1.25rem', borderRight: '1px solid #475569', color: '#34d399', fontSize: '1rem' }}>
                      {formatMoney(block.total.caTotal)}
                    </td>
                  </tr>
                </React.Fragment>
              );
            })}

            {/* LIGNE TOTAL GENERAL */}
            <tr style={{
              background: '#020617',
              color: '#ffffff',
              fontWeight: '900',
              fontSize: '1rem',
              borderTop: '3px solid #0f172a',
              borderBottom: '3px double #0f172a'
            }}>
              <td
                onClick={() => openStudentsModal('TOTAL GENERAL', tdbData.grandTotal.students_total)}
                style={{
                  padding: '0.95rem 1.25rem',
                  borderRight: '1px solid #334155',
                  borderLeft: '1px solid #334155',
                  letterSpacing: '0.05em',
                  cursor: 'pointer'
                }}
                title="Cliquer pour voir tous les étudiants du total général"
              >
                TOTAL GENERAL 🔍
              </td>
              <td style={{ textAlign: 'center', padding: '0.95rem 0.4rem', borderRight: '1px solid #334155', fontSize: '1.05rem' }}>
                {renderCellNumber(tdbData.grandTotal.inscritsFI, 'TOTAL GENERAL (Inscrits FI)', tdbData.grandTotal.students_inscritsFI)}
              </td>
              <td style={{ textAlign: 'center', padding: '0.95rem 0.4rem', borderRight: '1px solid #334155', fontSize: '1.05rem' }}>
                {renderCellNumber(tdbData.grandTotal.inscritsTA, 'TOTAL GENERAL (Inscrits TA)', tdbData.grandTotal.students_inscritsTA)}
              </td>
              <td style={{ textAlign: 'center', padding: '0.95rem 0.4rem', borderRight: '1px solid #334155', fontSize: '1.05rem' }}>
                {renderCellNumber(tdbData.grandTotal.inscritsExec, 'TOTAL GENERAL (Inscrits Executive)', tdbData.grandTotal.students_inscritsExec)}
              </td>
              <td style={{ textAlign: 'center', padding: '0.95rem 0.4rem', borderRight: '1px solid #334155', fontSize: '1.05rem' }}>
                {renderCellNumber(tdbData.grandTotal.inscritsVAE, 'TOTAL GENERAL (Inscrits VAE)', tdbData.grandTotal.students_inscritsVAE)}
              </td>
              <td style={{ textAlign: 'center', padding: '0.95rem 0.4rem', borderRight: '1px solid #334155', fontSize: '1.05rem' }}>
                {renderCellNumber(tdbData.grandTotal.reinscritsFI, 'TOTAL GENERAL (Réinscrits FI)', tdbData.grandTotal.students_reinscritsFI)}
              </td>
              <td style={{ textAlign: 'center', padding: '0.95rem 0.4rem', borderRight: '1px solid #334155', fontSize: '1.05rem' }}>
                {renderCellNumber(tdbData.grandTotal.reinscritsTA, 'TOTAL GENERAL (Réinscrits TA)', tdbData.grandTotal.students_reinscritsTA)}
              </td>
              <td style={{ textAlign: 'center', padding: '0.95rem 0.4rem', borderRight: '1px solid #334155', fontSize: '1.05rem' }}>
                {renderCellNumber(tdbData.grandTotal.reinscritsExec, 'TOTAL GENERAL (Réinscrits Executive)', tdbData.grandTotal.students_reinscritsExec)}
              </td>
              <td style={{ textAlign: 'center', padding: '0.95rem 0.4rem', borderRight: '1px solid #334155', fontSize: '1.05rem' }}>
                {renderCellNumber(tdbData.grandTotal.reinscritsVAE, 'TOTAL GENERAL (Réinscrits VAE)', tdbData.grandTotal.students_reinscritsVAE)}
              </td>
              <td style={{ textAlign: 'right', padding: '0.95rem 1.25rem', borderRight: '1px solid #334155', color: '#10b981', fontSize: '1.15rem' }}>
                {formatMoney(tdbData.grandTotal.caTotal)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Note de bas de tableau */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginTop: '0.85rem',
        fontSize: '0.78rem',
        color: '#64748b'
      }}>
        <span>
          💡 Astuce : Cliquez sur n'importe quel chiffre (ex: <strong>27</strong>) ou sur les sous-totaux pour afficher la liste détaillée des étudiants correspondants.
        </span>
        <span>
          Total analysé : <strong>{tdbData.totalEtudiantsAnalyses}</strong> étudiants
        </span>
      </div>

      {/* ===== MODAL D'AFFICHAGE DE LA LISTE DES ÉTUDIANTS ===== */}
      {modalData.isOpen && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(15, 23, 42, 0.65)',
            backdropFilter: 'blur(4px)',
            zIndex: 99999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '1rem'
          }}
          onClick={closeStudentsModal}
        >
          <div
            style={{
              background: '#ffffff',
              borderRadius: '14px',
              width: '100%',
              maxWidth: '1050px',
              maxHeight: '90vh',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.35)',
              overflow: 'hidden',
              animation: 'fadeInModal 0.2s ease-out'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div style={{
              background: 'linear-gradient(135deg, #0f172a 0%, #1e3a8a 100%)',
              color: '#ffffff',
              padding: '1.1rem 1.5rem',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              borderBottom: '1px solid rgba(255,255,255,0.1)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <div style={{
                  background: 'rgba(255,255,255,0.15)',
                  padding: '0.5rem',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Users size={22} color="#ffffff" />
                </div>
                <div>
                  <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: '800', color: '#ffffff' }}>
                    {modalData.title}
                  </h3>
                  <span style={{ fontSize: '0.8rem', color: '#93c5fd' }}>
                    Année Universitaire {anneeScolaire} — Liste nominative détaillée
                  </span>
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <span style={{
                  background: '#2563eb',
                  color: '#ffffff',
                  padding: '0.3rem 0.8rem',
                  borderRadius: '9999px',
                  fontWeight: '800',
                  fontSize: '0.85rem'
                }}>
                  {modalData.students.length} étudiant(s)
                </span>
                <button
                  onClick={closeStudentsModal}
                  style={{
                    background: 'rgba(255, 255, 255, 0.1)',
                    border: 'none',
                    borderRadius: '8px',
                    color: '#ffffff',
                    width: '34px',
                    height: '34px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    transition: 'background-color 0.15s'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.25)'}
                  onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)'}
                >
                  <X size={20} />
                </button>
              </div>
            </div>

            {/* Modal Toolbar : Recherche & Export CSV */}
            <div style={{
              padding: '0.85rem 1.5rem',
              background: '#f8fafc',
              borderBottom: '1px solid #e2e8f0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '0.75rem'
            }}>
              <div style={{ position: 'relative', width: '320px', maxWidth: '100%' }}>
                <Search size={16} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
                <input
                  type="text"
                  value={rechercheModal}
                  onChange={(e) => setRechercheModal(e.target.value)}
                  placeholder="Filtrer par nom, code, tél, email..."
                  style={{
                    width: '100%',
                    padding: '0.45rem 0.75rem 0.45rem 2.1rem',
                    borderRadius: '6px',
                    border: '1px solid #cbd5e1',
                    fontSize: '0.85rem',
                    outline: 'none'
                  }}
                  autoFocus
                />
              </div>

              <button
                onClick={handleExportModalList}
                style={{
                  padding: '0.45rem 1rem',
                  background: '#16a34a',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '6px',
                  fontWeight: '700',
                  fontSize: '0.85rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.4rem',
                  cursor: 'pointer',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                }}
              >
                <FileSpreadsheet size={16} />
                Exporter en Excel
              </button>
            </div>

            {/* Modal Body : Table des étudiants */}
            <div style={{ overflowY: 'auto', flex: 1, padding: '1rem 1.5rem' }}>
              {modalStudentsFiltres.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '3rem 1rem', color: '#64748b' }}>
                  <Users size={42} style={{ color: '#cbd5e1', marginBottom: '0.5rem' }} />
                  <p style={{ fontWeight: '600', margin: '0.5rem 0 0 0' }}>Aucun étudiant trouvé</p>
                  <p style={{ fontSize: '0.85rem', margin: '0.25rem 0 0 0' }}>Essayez un autre mot-clé dans la recherche</p>
                </div>
              ) : (
                <table style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: '0.85rem',
                  textAlign: 'left'
                }}>
                  <thead>
                    <tr style={{ background: '#f1f5f9', borderBottom: '2px solid #cbd5e1', color: '#334155' }}>
                      <th style={{ padding: '0.65rem 0.5rem', textAlign: 'center', width: '40px', fontWeight: '700' }}>#</th>
                      <th style={{ padding: '0.65rem 0.75rem', fontWeight: '700' }}>Code Étudiant</th>
                      <th style={{ padding: '0.65rem 0.75rem', fontWeight: '700' }}>Nom & Prénom</th>
                      <th style={{ padding: '0.65rem 0.75rem', fontWeight: '700' }}>Téléphone</th>
                      <th style={{ padding: '0.65rem 0.75rem', fontWeight: '700' }}>Email</th>
                      <th style={{ padding: '0.65rem 0.75rem', fontWeight: '700' }}>Filière & Niveau</th>
                      <th style={{ padding: '0.65rem 0.75rem', fontWeight: '700' }}>Mode</th>
                      <th style={{ padding: '0.65rem 0.75rem', fontWeight: '700' }}>Statut</th>
                      <th style={{ padding: '0.65rem 0.75rem', textAlign: 'right', fontWeight: '700' }}>Prix Validé</th>
                    </tr>
                  </thead>
                  <tbody>
                    {modalStudentsFiltres.map((e, idx) => {
                      const mode = (e.niveauFormation || 'FI').trim();
                      const isNew = e.nouvelleInscription !== false;
                      const prix = (e.isPartner ? (parseFloat(e.prixTotalPartner) || parseFloat(e.prixTotal)) : parseFloat(e.prixTotal)) || 0;
                      const isEven = idx % 2 === 0;

                      return (
                        <tr
                          key={e._id || idx}
                          style={{
                            background: isEven ? '#ffffff' : '#f8fafc',
                            borderBottom: '1px solid #e2e8f0',
                            transition: 'background-color 0.15s'
                          }}
                          onMouseOver={(ev) => ev.currentTarget.style.backgroundColor = '#f1f5f9'}
                          onMouseOut={(ev) => ev.currentTarget.style.backgroundColor = isEven ? '#ffffff' : '#f8fafc'}
                        >
                          <td style={{ textAlign: 'center', padding: '0.55rem 0.5rem', color: '#94a3b8', fontWeight: '600' }}>
                            {idx + 1}
                          </td>
                          <td style={{ padding: '0.55rem 0.75rem' }}>
                            <span style={{
                              fontFamily: 'monospace',
                              fontWeight: '700',
                              color: '#1e40af',
                              background: '#eff6ff',
                              padding: '2px 6px',
                              borderRadius: '4px',
                              border: '1px solid #dbeafe',
                              fontSize: '0.8rem'
                            }}>
                              {e.codeEtudiant || '—'}
                            </span>
                          </td>
                          <td style={{ padding: '0.55rem 0.75rem', fontWeight: '700', color: '#0f172a' }}>
                            {e.nomDeFamille} {e.prenom}
                          </td>
                          <td style={{ padding: '0.55rem 0.75rem', color: '#334155' }}>
                            {e.telephone ? (
                              <a
                                href={`tel:${e.telephone}`}
                                style={{ color: '#0284c7', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '4px' }}
                              >
                                <Phone size={13} />
                                {e.telephone}
                              </a>
                            ) : '—'}
                          </td>
                          <td style={{ padding: '0.55rem 0.75rem', color: '#475569', fontSize: '0.8rem' }}>
                            {e.email ? (
                              <a
                                href={`mailto:${e.email}`}
                                style={{ color: '#475569', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '4px' }}
                              >
                                <Mail size={13} />
                                {e.email}
                              </a>
                            ) : '—'}
                          </td>
                          <td style={{ padding: '0.55rem 0.75rem', color: '#1e293b' }}>
                            <span style={{ fontWeight: '600' }}>{e.filiere || '—'}</span>
                            {e.niveau && <span style={{ color: '#64748b' }}> — Niveau {e.niveau}</span>}
                            {(e.specialite || e.option) && (
                              <div style={{ fontSize: '0.75rem', color: '#64748b' }}>
                                {e.specialite || e.option}
                              </div>
                            )}
                          </td>
                          <td style={{ padding: '0.55rem 0.75rem' }}>
                            <span style={{
                              background: mode === 'TA' ? '#fef3c7' : mode === 'VAE' ? '#f3e8ff' : mode.includes('Exec') ? '#ffedd5' : '#e0f2fe',
                              color: mode === 'TA' ? '#92400e' : mode === 'VAE' ? '#6b21a8' : mode.includes('Exec') ? '#9a3412' : '#0369a1',
                              padding: '2px 7px',
                              borderRadius: '4px',
                              fontWeight: '700',
                              fontSize: '0.75rem'
                            }}>
                              {mode}
                            </span>
                          </td>
                          <td style={{ padding: '0.55rem 0.75rem' }}>
                            <span style={{
                              background: isNew ? '#ecfdf5' : '#f1f5f9',
                              color: isNew ? '#065f46' : '#475569',
                              padding: '2px 6px',
                              borderRadius: '4px',
                              fontWeight: '600',
                              fontSize: '0.75rem'
                            }}>
                              {isNew ? 'Nouveau' : 'Réinscrit'}
                            </span>
                          </td>
                          <td style={{ padding: '0.55rem 0.75rem', textAlign: 'right', fontWeight: '700', color: '#0f172a' }}>
                            {formatMoney(prix)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>

            {/* Modal Footer */}
            <div style={{
              padding: '0.85rem 1.5rem',
              background: '#f8fafc',
              borderTop: '1px solid #e2e8f0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <span style={{ fontSize: '0.82rem', color: '#64748b' }}>
                Affichage de <strong>{modalStudentsFiltres.length}</strong> sur <strong>{modalData.students.length}</strong> étudiants
              </span>
              <button
                onClick={closeStudentsModal}
                style={{
                  padding: '0.5rem 1.25rem',
                  background: '#0f172a',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '6px',
                  fontWeight: '700',
                  fontSize: '0.85rem',
                  cursor: 'pointer'
                }}
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TdbPedagogiqueComponent;
